import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { Perfil, Topico, Subtopico, EcadAutomacaoPasso } from './types';
import { api } from '../../../api/client';
import { useEcadStore } from './store/ecadStore';

function getWelcomeMessageForPerfil(nomePerfil: string): string {
  const nomeNorm = nomePerfil.toLowerCase();
  if (nomeNorm.includes('aluno')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Alunos**. Posso ajudar a planejar e automatizar roteiros de treinamento para o portal do aluno, como visualização de notas, boletins, frequência, visualização de horários e auto-atendimento. Como posso te ajudar a construir esta automação hoje?';
  }
  if (nomeNorm.includes('coordenador')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Coordenadores**. Posso te orientar na criação de automações para tarefas de coordenação pedagógica, como grade horária, distribuição de turmas, planejamento pedagógico e acompanhamento de professores. O que deseja automatizar hoje?';
  }
  if (nomeNorm.includes('diretor')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Diretores**. Estou pronto para auxiliar na elaboração de roteiros focados em gestão escolar de alto nível, incluindo relatórios gerenciais, auditorias, autorizações gerais e estatísticas da escola. Qual processo de gestão vamos simular hoje?';
  }
  if (nomeNorm.includes('equipe interna') || nomeNorm.includes('interno') || nomeNorm.includes('equipe') || nomeNorm.includes('administrador') || nomeNorm.includes('admin')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Administrador**. Posso ajudar na automação de processos internos críticos, parametrização do sistema, controle de permissões globais, logs de auditoria e manutenção de dados mestre. Qual fluxo de suporte ou administração interna faremos hoje?';
  }
  if (nomeNorm.includes('prefeitura')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Prefeituras**. Posso te orientar a estruturar automações relativas à gestão da rede municipal, convênios, repasse de recursos, controle de frotas escolares ou comunicação institucional da Secretaria de Educação. O que gostaria de mapear hoje?';
  }
  if (nomeNorm.includes('professor')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Professores**. Posso te ajudar na automação de tarefas diárias do docente, como lançamento de notas, diário de classe, registro de frequência, ocorrências e planos de aula semanais. Qual dessas rotinas pedagógicas vamos construir ou testar hoje?';
  }
  if (nomeNorm.includes('responsá') || nomeNorm.includes('responsavel') || nomeNorm.includes('pais')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Responsáveis**. Estou pronto para auxiliar na criação de fluxos de treinamento voltados para pais e responsáveis, como justificar faltas, acompanhar ocorrências, assinar comunicados e interagir com a direção. Como posso ajudar na automação hoje?';
  }
  if (nomeNorm.includes('secretaria')) {
    return 'Olá! Sou o assistente do ECAD AI Studio para o perfil **Secretarias**. Posso ajudar na automação de processos da secretaria escolar, como matrículas de novos alunos, emissão de declarações, histórico escolar e transferência de turmas. O que deseja automatizar hoje?';
  }
  return `Olá! Sou o assistente do ECAD AI Studio para o perfil **${nomePerfil}**. Posso ajudar você a criar novos roteiros de treinamento, sugerir passos de automação do Playwright ou validar seletores CSS específicos para este perfil. O que deseja fazer hoje?`;
}

export function normalizeTipoAcao(tipo: string): string {
  if (!tipo) return 'EXPLICAR';
  const u = tipo.toUpperCase().trim();
  if (u === 'NAVIGATE') return 'ESPERAR_PAGINA';
  if (u === 'CLICK') return 'MOVER_E_CLICAR';
  if (u === 'TYPE' || u === 'FILL') return 'DIGITAR';
  if (u === 'WAIT') return 'ESPERAR_TEMPO';
  if (u === 'ASSERT' || u === 'SCREENSHOT' || u === 'HIGHLIGHT') return 'DESTACAR';
  return u;
}

export function sanitizeAcaoValor(tipoAcao: string, valor: string): string {
  if (!valor) return '';
  const normalizedTipo = normalizeTipoAcao(tipoAcao);
  if (normalizedTipo === 'ESPERAR_PAGINA') {
    let cleaned = valor.trim();
    // Remover quaisquer ocorrências de extensões .html e remapear caminhos comuns
    if (cleaned.endsWith('.html')) {
      cleaned = cleaned.substring(0, cleaned.length - 5);
    }
    cleaned = cleaned.replace(/\.html/g, '');

    // Mapeamentos específicos
    if (cleaned === 'login' || cleaned === '/login') {
      return '/login';
    }
    if (cleaned === 'admin' || cleaned === '/admin') {
      return '/admin/dashboard';
    }
    if (cleaned === 'ativar' || cleaned === '/ativar') {
      return '/ativar';
    }
    if (cleaned === 'conectando' || cleaned === '/conectando') {
      return '/conectando';
    }

    // Se não começar com http/https nem barra, coloca barra
    if (!cleaned.startsWith('http://') && !cleaned.startsWith('https://') && !cleaned.startsWith('/')) {
      cleaned = '/' + cleaned;
    }
    return cleaned;
  }
  return valor;
}


export function useEcadStudio(perfis: Perfil[], onRefreshCatalog: () => void) {
  // UI Map para smart dropdowns de seletores (pageKey → { elementKey → { seletor, descricao } })
  const [uiMap, setUiMap] = useState<Record<string, Record<string, { seletor: string; descricao: string }>>>({});

  useEffect(() => {
    api.get('/ecad/ui-map')
      .then(res => {
        if (res.data?.ok && res.data.uiMap) {
          setUiMap(res.data.uiMap);
        }
      })
      .catch(err => console.error('Falha ao carregar ui_map:', err));
  }, []);

  // ═══ Persistência de seleção via URL Query Params (sobrevive a F5) ═══
  const getUrlParam = (key: string): string | null => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(key);
    } catch { return null; }
  };

  // Roteiro / Subtópico Selecionado no Estúdio
  const [selectedSubtopicoId, setSelectedSubtopicoId] = useState<string | null>(getUrlParam('roteiro'));
  const [selectedRoteiro, setSelectedRoteiro] = useState<Subtopico | null>(null);

  // Perfil Selecionado no Estúdio (Escopo Ativo)
  const [selectedPerfilId, setSelectedPerfilId] = useState<string | null>(getUrlParam('perfil'));

  // Tópico Selecionado no Estúdio (Escopo Ativo)
  const [selectedTopicoId, setSelectedTopicoId] = useState<string | null>(getUrlParam('topico'));

  // Sincronizar seleção → URL (sem recarregar a página)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedPerfilId) params.set('perfil', selectedPerfilId); else params.delete('perfil');
    if (selectedTopicoId) params.set('topico', selectedTopicoId); else params.delete('topico');
    if (selectedSubtopicoId) params.set('roteiro', selectedSubtopicoId); else params.delete('roteiro');

    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
    window.history.replaceState(null, '', newUrl);
  }, [selectedPerfilId, selectedTopicoId, selectedSubtopicoId]);

  // Lista de Passos de Automação do Roteiro Ativo
  const [passos, setPassos] = useState<EcadAutomacaoPasso[]>([]);

  // Loading Geral de Roteiro/Passos
  const [loadingRoteiro, setLoadingRoteiro] = useState(false);
  const [savingPassos, setSavingPassos] = useState(false);

  // Toast notification (substitui alert nativo)
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(msg);
    setToastType(type);
    if (type === 'success') {
      toast.success(msg);
    } else {
      toast.error(msg);
    }
    toastTimeoutRef.current = setTimeout(() => setToastMessage(null), 4000);
  };

  // Estados de Modais
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [showTopicoModal, setShowTopicoModal] = useState(false);
  const [showSubtopicoModal, setShowSubtopicoModal] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);

  // Controle de Ordem e Arquivamento Visual
  const [customTopicOrder, setCustomTopicOrder] = useState<string[]>([]);

  useEffect(() => {
    if (selectedPerfilId) {
      try {
        const saved = localStorage.getItem(`ecad_custom_topic_order_${selectedPerfilId}`);
        setCustomTopicOrder(saved ? JSON.parse(saved) : []);
      } catch (e) {
        setCustomTopicOrder([]);
      }
    } else {
      setCustomTopicOrder([]);
    }
  }, [selectedPerfilId]);

  const [perfilForm, setPerfilForm] = useState({ id: '', nome: '', isInterno: false });
  const [topicoForm, setTopicoForm] = useState({ id: '', perfilId: '', titulo: '' });
  const [subtopicoForm, setSubtopicoForm] = useState({
    id: '',
    topicoId: '',
    titulo: '',
    descricao: '',
    duracao: '2m 30s',
    dificuldade: 'Básico',
    videoUrl: '',
    pdfUrl: ''
  });

  // Estados de Edição do Roteiro (Campos Principais)
  const [roteiroEditForm, setRoteiroEditForm] = useState({
    titulo: '',
    descricao: '',
    videoUrl: '',
    pdfUrl: '',
    duracao: '',
    dificuldade: '',
    urlInicial: 'http://localhost:3443',
    velocidadeDigitacaoMs: 60,
    mouseSteps: 45,
    vozTutor: 'Kore'
  });
  const [savingRoteiroMeta, setSavingRoteiroMeta] = useState(false);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [isEditingPassos, setIsEditingPassos] = useState(false);
  const [zoomImage, setZoomImage] = useState<{ stepIndex: number; type: 'real' | 'foco' } | null>(null);

  const handleCancelEditMeta = () => {
    if (selectedRoteiro) {
      setRoteiroEditForm({
        titulo: selectedRoteiro.titulo || '',
        descricao: selectedRoteiro.descricao || '',
        videoUrl: selectedRoteiro.videoUrl || '',
        pdfUrl: selectedRoteiro.pdfUrl || '',
        duracao: selectedRoteiro.duracao || '',
        dificuldade: selectedRoteiro.dificuldade || 'Básico',
        urlInicial: selectedRoteiro.config?.urlInicial || 'http://localhost:3443',
        velocidadeDigitacaoMs: selectedRoteiro.config?.velocidadeDigitacaoMs || 60,
        mouseSteps: selectedRoteiro.config?.mouseSteps || 45,
        vozTutor: selectedRoteiro.config?.vozTutor || 'Kore'
      });
    }
    setIsEditingMetadata(false);
  };

  // Estados de Interface por Abas
  const [activeTab, setActiveTab] = useState<'editor' | 'midias' | 'simulacao' | 'restaurar'>('editor');

  // Estados para o sistema de snapshot (Restaurar)
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [restoringSnapshot, setRestoringSnapshot] = useState(false);

  const [passosEditMode, setPassosEditMode] = useState<'visual' | 'json'>('visual');
  const [jsonValue, setJsonValue] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfProgressShow, setPdfProgressShow] = useState(false);
  const [pdfProgressStep, setPdfProgressStep] = useState(1);
  const [pdfProgressError, setPdfProgressError] = useState<string | null>(null);
  const [pdfProgressUrl, setPdfProgressUrl] = useState<string | null>(null);
  const [pdfLogs, setPdfLogs] = useState<string[]>([]);

  // Estados do Scanner de Página
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [scannerForm, setScannerForm] = useState({ url: 'http://localhost:3443', pageKey: 'landing_page' });
  const [scanningPage, setScanningPage] = useState(false);

  // Sincroniza o valor de texto do JSON quando os passos mudarem
  useEffect(() => {
    try {
      setJsonValue(JSON.stringify(passos.map(p => ({
        ordem: p.ordem,
        tipoAcao: p.tipoAcao,
        selectorCss: p.selectorCss,
        valor: p.valor || '',
        legenda: p.legenda || ''
      })), null, 2));
    } catch (e) {
      console.error('Erro ao serializar passos para JSON:', e);
    }
  }, [passos]);

  // Estados de Simulação (Dry-Run)
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationLogs, setSimulationLogs] = useState<string[]>([]);
  const [simulationSuccess, setSimulationSuccess] = useState<boolean | null>(null);
  const [simulationModeVisible, setSimulationModeVisible] = useState(true);
  const terminalEndRef = useRef<HTMLDivElement>(null);

  // Estados de Dry-Run (validação rápida de seletores)
  const [isDryRunning, setIsDryRunning] = useState(false);
  const [dryRunReport, setDryRunReport] = useState<Array<{
    ordem: number;
    acao: string;
    selectorOriginal: string;
    selectorResolvido: string | null;
    status: 'PASS' | 'FAIL' | 'SKIP' | 'NO_SELECTOR';
    motivo?: string;
  }> | null>(null);

  // Estados de Simulação adicionais para visualização em Stream
  const [activeStepOrdem, setActiveStepOrdem] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [streamFrame, setStreamFrame] = useState<string | null>(null);
  const [currentBrowserUrl, setCurrentBrowserUrl] = useState<string>('');
  const [isPaused, setIsPaused] = useState(false);
  const [precacheInfo, setPrecacheInfo] = useState<{ active: boolean; total: number; completed: number } | null>(null);
  const [browserInstance, setBrowserInstance] = useState<{
    active: boolean;
    headless: boolean | null;
    startTime: number | null;
  } | null>(null);

  // Dry-Run Panel states
  const [showDryRunPanel, setShowDryRunPanel] = useState(false);
  const [dryRunFullReport, setDryRunFullReport] = useState<{
    report: any[];
    durationMs: number;
    aprovados: number;
    falhas: number;
    pulados: number;
  } | null>(null);
  const [retryingStep, setRetryingStep] = useState<number | null>(null);
  const [openingStep, setOpeningStep] = useState<number | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);

  // Conexão do WebSocket do stream em tempo real
  useEffect(() => {
    if (selectedSubtopicoId) {
      const wsUrl = `ws://${window.location.hostname}:3335`;
      console.log(`[EcadStudio] Conectando ao WebSocket do simulador: ${wsUrl}`);

      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl);
      } catch (err) {
        console.error('[EcadStudio] Falha ao criar WebSocket client:', err);
        return;
      }

      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[EcadStudio] Conectado ao stream do simulador!');
        setConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'frame') {
            const canvas = useEcadStore.getState().streamCanvas;
            if (canvas && msg.data.image) {
              const ctx = canvas.getContext('2d');
              if (ctx) {
                const img = new Image();
                img.onload = () => {
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                };
                img.src = msg.data.image;
              }
            }
            if (!streamFrame) {
              setStreamFrame('active');
            }
            if (msg.data.activeStep) {
              setActiveStepOrdem(msg.data.activeStep);
            }
            if (msg.data.url) {
              setCurrentBrowserUrl(msg.data.url);
            }
          } else if (msg.event === 'step-active') {
            setActiveStepOrdem(msg.data.ordem);
            if (activeAudioRef.current) {
              activeAudioRef.current.pause();
              activeAudioRef.current.src = "";
            }
          } else if (msg.event === 'step-error') {
            // ── Erro em passo: tocar som de erro + mostrar detalhes ──
            setActiveStepOrdem(msg.data.ordem);
            const errorInfo = {
              ordem: msg.data.ordem,
              tipoAcao: msg.data.tipoAcao,
              seletor: msg.data.seletor,
              erro: msg.data.erro,
              screenshot: msg.data.screenshot ? `data:image/jpeg;base64,${msg.data.screenshot}` : null,
            };
            if (errorInfo.screenshot) {
              const canvas = useEcadStore.getState().streamCanvas;
              if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  const img = new Image();
                  img.onload = () => {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  };
                  img.src = errorInfo.screenshot;
                }
              }
            }
            if (!streamFrame) {
              setStreamFrame('active');
            }
            setSimulationLogs(prev => [
              ...prev,
              `⛔ ERRO [Passo ${msg.data.ordem}] ${msg.data.tipoAcao}: ${msg.data.erro}`,
            ]);
            // Tocar beep de erro via Web Audio API
            try {
              const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
              const osc = audioCtx.createOscillator();
              const gain = audioCtx.createGain();
              osc.connect(gain);
              gain.connect(audioCtx.destination);
              osc.type = 'square';
              osc.frequency.value = 440;
              gain.gain.value = 0.15;
              osc.start();
              osc.stop(audioCtx.currentTime + 0.25);
              setTimeout(() => {
                const osc2 = audioCtx.createOscillator();
                const gain2 = audioCtx.createGain();
                osc2.connect(gain2);
                gain2.connect(audioCtx.destination);
                osc2.type = 'square';
                osc2.frequency.value = 330;
                gain2.gain.value = 0.15;
                osc2.start();
                osc2.stop(audioCtx.currentTime + 0.35);
              }, 280);
            } catch (e) { }
          } else if (msg.event === 'status') {
            if (msg.data.browserInstance !== undefined) {
              setBrowserInstance(msg.data.browserInstance);
            }
            if (msg.data.state === 'precaching') {
              setPrecacheInfo({ active: true, total: msg.data.total || 0, completed: msg.data.completed || 0 });
            } else if (msg.data.state === 'precaching-done') {
              setPrecacheInfo(null);
            } else if (msg.data.state === 'simulating') {
              setPrecacheInfo(null);
              setIsSimulating(true);
              setIsPaused(false);
              if (msg.data.activeStep) {
                setActiveStepOrdem(msg.data.activeStep);
              }
            } else if (msg.data.state === 'paused') {
              setIsPaused(true);
              if (msg.data.activeStep) {
                setActiveStepOrdem(msg.data.activeStep);
              }
              if (activeAudioRef.current) {
                activeAudioRef.current.pause();
              }
            } else if (msg.data.state === 'stopped') {
              setPrecacheInfo(null);
              setIsSimulating(false);
              setIsPaused(false);
              setActiveStepOrdem(null);
              if (activeAudioRef.current) {
                activeAudioRef.current.pause();
                activeAudioRef.current.src = "";
                activeAudioRef.current = null;
              }
            }
          } else if (msg.event === 'url-change') {
            setCurrentBrowserUrl(msg.data.url);
          } else if (msg.event === 'log') {
            const logLine = msg.data || '';
            setSimulationLogs(prev => [...prev, logLine]);
            setPdfLogs(prev => [...prev, logLine]);

            // Mapear dinamicamente os passos do progresso do PDF com base nos logs reais
            if (logLine.includes('Processando Passo')) {
              setPdfProgressStep(2);
            } else if (logLine.includes('CONSTRUINDO DOCUMENTO HTML')) {
              setPdfProgressStep(3);
            } else if (logLine.includes('RENDERIZANDO PDF A PARTIR DO HTML')) {
              setPdfProgressStep(4);
            } else if (logLine.includes('MANUAL EM PDF GERADO COM SUCESSO') || logLine.includes('Manual PDF gerado e atualizado no banco')) {
              setPdfProgressStep(5);
            }
          } else if (msg.event === 'audio') {
            if (msg.data.audio) {
              if (activeAudioRef.current) {
                activeAudioRef.current.pause();
                activeAudioRef.current.src = "";
              }
              const audioUrl = `data:audio/mp3;base64,${msg.data.audio}`;
              const audioObj = new Audio(audioUrl);
              activeAudioRef.current = audioObj;
              audioObj.play().catch(e => console.error('[EcadStudio] Erro ao reproduzir audio:', e));
            }
          }
        } catch (e) {
          console.error('[EcadStudio] Erro ao ler mensagem do simulador:', e);
        }
      };

      ws.onclose = () => {
        console.log('[EcadStudio] Desconectado do simulador');
        setConnected(false);
        wsRef.current = null;
      };

      ws.onerror = (err) => {
        console.error('[EcadStudio] Erro no WebSocket do simulador:', err);
      };

      return () => {
        ws.close();
        wsRef.current = null;
      };
    }
  }, [selectedSubtopicoId]);

  const sendControlCommand = (command: string, extra = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ command, ...extra }));
    }
  };

  const handleStopSimulation = () => {
    sendControlCommand('stop');
    setIsSimulating(false);
    setIsPaused(false);
    setActiveStepOrdem(null);
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current.src = "";
      activeAudioRef.current = null;
    }
  };

  const handlePauseSimulation = () => {
    sendControlCommand('pause');
    setIsPaused(true);
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
    }
  };

  const handleJumpToStep = (ordem: number) => {
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
    }
    sendControlCommand('jump-to-step', { ordem });
  };

  const handleResumeSimulation = () => {
    sendControlCommand('resume');
    setIsPaused(false);
  };

  const handleStepForward = () => {
    sendControlCommand('step-forward');
  };

  const handleStepBackward = () => {
    sendControlCommand('step-backward');
  };

  const handleBackBrowser = () => {
    sendControlCommand('back');
  };

  const handleForwardBrowser = () => {
    sendControlCommand('forward');
  };

  const handleReloadBrowser = () => {
    sendControlCommand('reload');
  };

  const handleNavigateBrowser = (url: string) => {
    sendControlCommand('navigate', { url });
  };

  const handleKillBrowser = () => {
    sendControlCommand('kill-browser');
  };

  const handleRestartBrowser = () => {
    sendControlCommand('restart-browser');
  };

  // Estados do Copiloto AI Gemini
  const [copilotMessages, setCopilotMessages] = useState<Array<{ role: 'user' | 'assistant', text: string }>>([
    {
      role: 'assistant',
      text: 'Olá! Sou o assistente de gravação e automação do ECAD AI Studio. Posso ajudar você a criar novos roteiros de treinamento, sugerir passos de automação do Playwright para os formulários do Aluno Conectado ou validar seus seletores CSS. O que deseja fazer hoje?'
    }
  ]);
  const [copilotInput, setCopilotInput] = useState('');
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [selectedStepIndexes, setSelectedStepIndexes] = useState<number[]>([]);
  const [proposedUiActions, setProposedUiActions] = useState<any[] | null>(null);
  const [activeEditingIndex, setActiveEditingIndex] = useState<number | null>(null);
  const [includePerfilContext, setIncludePerfilContext] = useState<boolean>(true);
  const [includeTopicoContext, setIncludeTopicoContext] = useState<boolean>(true);
  const [includeSubtopicoContext, setIncludeSubtopicoContext] = useState<boolean>(true);
  const [includePassosContext, setIncludePassosContext] = useState<boolean>(true);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Sessões do Copilot (histórico)
  const [copilotSessionHistory, setCopilotSessionHistory] = useState<Array<{
    id: string;
    label: string;
    timestamp: string;
    messages: Array<{ role: 'user' | 'assistant' | 'system'; text: string }>;
  }>>([]);
  const [activeSessionId, setActiveSessionId] = useState<string>(() => `session-${Date.now()}`);

  // Auto-scroll para logs e chats
  useEffect(() => {
    terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [simulationLogs]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [copilotMessages]);

  // Atualiza as mensagens iniciais do copiloto de acordo com o perfil selecionado
  useEffect(() => {
    if (selectedPerfilId) {
      const perfilObj = perfis.find(p => p.id === selectedPerfilId);
      if (perfilObj) {
        setCopilotMessages([
          {
            role: 'assistant',
            text: getWelcomeMessageForPerfil(perfilObj.nome)
          }
        ]);
      }
    } else {
      setCopilotMessages([
        {
          role: 'assistant',
          text: 'Olá! Sou o assistente de gravação e automação do ECAD AI Studio. Posso ajudar você a criar novos roteiros de treinamento, sugerir passos de automação do Playwright para os formulários do Aluno Conectado ou validar seus seletores CSS. O que deseja fazer hoje?'
        }
      ]);
    }
  }, [selectedPerfilId, perfis]);

  // Auto-selecionar o perfil correspondente quando um roteiro/subtópico for selecionado
  useEffect(() => {
    if (selectedSubtopicoId && perfis) {
      const parentPerfil = perfis.find(p =>
        p.topicos.some(t =>
          t.subtopicos.some(s => s.id === selectedSubtopicoId)
        )
      );
      if (parentPerfil) {
        setSelectedPerfilId(parentPerfil.id);
      }
    }
  }, [selectedSubtopicoId, perfis]);

  const attemptedLoginRef = useRef(false);

  // Auto-login de desenvolvimento caso não esteja autenticado
  useEffect(() => {
    const token = sessionStorage.getItem('ac_token') || localStorage.getItem('ac_token');
    if (!token && !attemptedLoginRef.current) {
      attemptedLoginRef.current = true;
      console.log('[ECAD Studio] Auto-login de desenvolvimento...');
      api.post('/auth/login', {
        email: 'admin@alunoconectado.com.br',
        senha: 'Admin@2026'
      })
        .then(res => res.data)
        .then(data => {
          if (data.access_token || data.token) {
            const jwt = data.access_token || data.token;
            localStorage.setItem('ac_token', jwt);
            if (data.user) {
              localStorage.setItem('ac_user', JSON.stringify(data.user));
              if (data.user.escolaId) localStorage.setItem('ac_escolaId', data.user.escolaId);
              if (data.user.nomeEscola) localStorage.setItem('ac_nomeEscola', data.user.nomeEscola);
              if (data.user.perfil) localStorage.setItem('ac_perfil', data.user.perfil);
              if (data.user.cargo) localStorage.setItem('ac_cargo', data.user.cargo);
            }
            console.log('[ECAD Studio] Auto-login concluído com sucesso!');
            onRefreshCatalog();

            if (selectedSubtopicoId) {
              const currentId = selectedSubtopicoId;
              setSelectedSubtopicoId(null);
              setTimeout(() => {
                setSelectedSubtopicoId(currentId);
              }, 100);
            } else {
              window.location.reload();
            }
          }
        })
        .catch(err => {
          console.error('[ECAD Studio] Falha no auto-login:', err);
        });
    }
  }, [selectedSubtopicoId, onRefreshCatalog]);

  // Carrega os dados completos do roteiro (passos) quando seleciona
  useEffect(() => {
    if (!selectedSubtopicoId) {
      setSelectedRoteiro(null);
      setPassos([]);
      return;
    }

    setLoadingRoteiro(true);
    api.get(`/ecad/roteiro/${selectedSubtopicoId}`)
      .then((res) => {
        const data = res.data;
        if (data.ok && data.roteiro) {
          setSelectedRoteiro(data.roteiro);
          const loadedPassos = data.roteiro.passos || [];
          setPassos(loadedPassos);
          setRoteiroEditForm({
            titulo: data.roteiro.titulo || '',
            descricao: data.roteiro.descricao || '',
            videoUrl: data.roteiro.videoUrl || '',
            pdfUrl: data.roteiro.pdfUrl || '',
            duracao: data.roteiro.duracao || '',
            dificuldade: data.roteiro.dificuldade || 'Básico',
            urlInicial: data.roteiro.config?.urlInicial || 'http://localhost:3443',
            velocidadeDigitacaoMs: data.roteiro.config?.velocidadeDigitacaoMs || 60,
            mouseSteps: data.roteiro.config?.mouseSteps || 45,
            vozTutor: data.roteiro.config?.vozTutor || 'Kore'
          });

          // Se não houver passos cadastrados, apenas configura os contextos.
          // O usuário pode clicar manualmente no botão "Gerar Roteiro de Passos"
          // no Studio para enviar à IA quando desejar.
          if (loadedPassos.length === 0) {
            setIncludePerfilContext(false);
            setIncludeTopicoContext(false);
            setIncludeSubtopicoContext(true);
          }
        }
        setLoadingRoteiro(false);
      })
      .catch((err) => {
        console.error('Erro ao carregar roteiro completo:', err);
        setLoadingRoteiro(false);
      });
  }, [selectedSubtopicoId]);

  // Salvar metadados do Roteiro
  const handleSaveRoteiroMeta = async () => {
    if (!selectedRoteiro) return;
    setSavingRoteiroMeta(true);
    try {
      const config = {
        urlInicial: roteiroEditForm.urlInicial,
        velocidadeDigitacaoMs: Number(roteiroEditForm.velocidadeDigitacaoMs),
        mouseSteps: Number(roteiroEditForm.mouseSteps),
        vozTutor: roteiroEditForm.vozTutor,
      };

      const res = await api.post(`/ecad/roteiro`, {
        id: selectedRoteiro.id,
        topicoId: selectedRoteiro.topicoId,
        titulo: roteiroEditForm.titulo,
        descricao: roteiroEditForm.descricao,
        videoUrl: roteiroEditForm.videoUrl,
        pdfUrl: roteiroEditForm.pdfUrl,
        duracao: roteiroEditForm.duracao,
        dificuldade: roteiroEditForm.dificuldade,
        config
      });
      const data = res.data;
      if (data.ok) {
        showToast('Metadados do roteiro salvos com sucesso!', 'success');
        setIsEditingMetadata(false);
        setSelectedRoteiro(prev => prev ? {
          ...prev,
          ...roteiroEditForm,
          config
        } : null);
        onRefreshCatalog();
      } else {
        showToast('Erro ao salvar roteiro: ' + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Falha na comunicação com o backend.', 'error');
    } finally {
      setSavingRoteiroMeta(false);
    }
  };

  // Salvar passos de Automação no Banco
  const handleSavePassos = async (passosParaSalvar?: EcadAutomacaoPasso[], silent = false) => {
    if (!selectedSubtopicoId) return;
    setSavingPassos(true);
    const targetPassos = passosParaSalvar || passos;
    try {
      const passosFormatados = targetPassos.map((p, index) => ({
        ordem: index + 1,
        tipoAcao: normalizeTipoAcao(p.tipoAcao),
        selectorCss: typeof p.selectorCss === 'string' ? p.selectorCss.trim() : '',
        valor: sanitizeAcaoValor(p.tipoAcao, typeof p.valor === 'string' ? p.valor.trim() : ''),
        legenda: typeof p.legenda === 'string' ? p.legenda.trim() : '',
        urlAtual: typeof p.urlAtual === 'string' ? p.urlAtual.trim() : '',
        autenticacaoNecessaria: !!p.autenticacaoNecessaria
      }));

      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/passos`, { passos: passosFormatados });
      const data = res.data;
      if (data.ok) {
        if (!silent) {
          showToast('Passos de automação salvos com sucesso!', 'success');
        }
        setPassos(data.passos || targetPassos);
      } else {
        showToast('Erro ao salvar passos: ' + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Falha de comunicação com o backend.', 'error');
    } finally {
      setSavingPassos(false);
    }
  };

  // Salvar a partir do editor JSON
  const handleSaveFromJson = async () => {
    try {
      const parsed = JSON.parse(jsonValue);
      if (!Array.isArray(parsed)) {
        showToast('O JSON deve ser um array de passos.', 'error');
        return;
      }

      const validated: EcadAutomacaoPasso[] = parsed.map((p: any, idx: number) => {
        if (!p.tipoAcao) {
          throw new Error(`O passo na posição ${idx + 1} está sem o campo 'tipoAcao'.`);
        }
        return {
          ordem: p.ordem || (idx + 1),
          tipoAcao: normalizeTipoAcao(p.tipoAcao),
          selectorCss: typeof p.selectorCss === 'string' ? p.selectorCss.trim() : '',
          valor: sanitizeAcaoValor(p.tipoAcao, typeof p.valor === 'string' ? p.valor.trim() : ''),
          legenda: typeof p.legenda === 'string' ? p.legenda.trim() : '',
          urlAtual: typeof p.urlAtual === 'string' ? p.urlAtual.trim() : '',
          autenticacaoNecessaria: !!p.autenticacaoNecessaria
        };
      });

      await handleSavePassos(validated);
    } catch (err: any) {
      showToast(`Erro na validação do JSON: ${err.message || String(err)}`, 'error');
    }
  };

  // Regenerar passos e normalizar CSS selectors baseados no ui_map.json
  const handleRegeneratePassos = async () => {
    if (!selectedSubtopicoId) return;
    setSavingPassos(true);
    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/regenerate`);
      const data = res.data;
      if (data.ok && data.roteiro) {
        setPassos(data.roteiro.passos || []);
        // Atualiza a visualização JSON
        try {
          setJsonValue(JSON.stringify((data.roteiro.passos || []).map((p: any) => ({
            ordem: p.ordem,
            tipoAcao: p.tipoAcao,
            selectorCss: p.selectorCss,
            valor: p.valor || '',
            legenda: p.legenda || ''
          })), null, 2));
        } catch (e) { }
        showToast('Passos de automação regenerados com sucesso com base no ui_map.json!', 'success');
      } else {
        showToast('Erro ao regenerar passos: ' + extractErrorMessage(data), 'error');
      }
    } catch (err: any) {
      console.error(err);
      showToast('Erro de conexão ao regenerar passos: ' + (err.message || String(err)), 'error');
    } finally {
      setSavingPassos(false);
    }
  };

  const fetchSnapshots = async (subtopicoId: string) => {
    setLoadingSnapshots(true);
    try {
      const res = await api.get(`/ecad/roteiro/${subtopicoId}/snapshots`);
      const data = res.data;
      if (data.ok) {
        setSnapshots(data.snapshots || []);
      }
    } catch (err) {
      console.error('Erro ao buscar snapshots:', err);
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const handleRestoreVersion = async (timestamp: string) => {
    if (!selectedSubtopicoId) return;
    if (!confirm('ATENÇÃO: Deseja realmente restaurar este ponto de backup? Isso substituirá as configurações de metadados e os passos de automação atuais. Os arquivos de vídeo/áudio do disco permanecerão preservados.')) {
      return;
    }

    setRestoringSnapshot(true);
    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/restore`, { timestamp });
      const data = res.data;
      if (data.ok) {
        showToast('Roteiro restaurado com sucesso para o ponto selecionado!', 'success');
        const currentId = selectedSubtopicoId;
        setSelectedSubtopicoId(null);
        setTimeout(() => {
          setSelectedSubtopicoId(currentId);
          setActiveTab('editor'); // Volta para a aba Roteiro
        }, 100);
      } else {
        showToast('Erro ao restaurar snapshot: ' + extractErrorMessage(data), 'error');
      }
    } catch (err: any) {
      console.error(err);
      showToast('Falha na comunicação com o backend ao restaurar: ' + (err.message || String(err)), 'error');
    } finally {
      setRestoringSnapshot(false);
    }
  };

  useEffect(() => {
    if (selectedSubtopicoId && activeTab === 'restaurar') {
      fetchSnapshots(selectedSubtopicoId);
    }
  }, [selectedSubtopicoId, activeTab]);

  // Disparar geração de manual PDF no backend
  const handleGeneratePdf = async (silent = false) => {
    if (!selectedSubtopicoId) return;
    setGeneratingPdf(true);
    setPdfProgressError(null);
    setPdfLogs([]); // Limpa logs anteriores antes de gerar

    if (!silent) {
      setPdfProgressShow(true);
      setPdfProgressStep(1);
      setPdfProgressError(null);
      setPdfProgressUrl(null);
    }

    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/pdf`);
      const data = res.data;
      if (data.ok && data.pdfUrl) {
        if (!silent) {
          setPdfProgressStep(5);
          setPdfProgressUrl(data.pdfUrl);
        }
        setRoteiroEditForm(prev => ({ ...prev, pdfUrl: data.pdfUrl }));
        if (selectedRoteiro) {
          setSelectedRoteiro({ ...selectedRoteiro, pdfUrl: data.pdfUrl });
        }
        onRefreshCatalog();
      } else {
        const errMsg = extractErrorMessage(data);
        setPdfProgressError(errMsg);
        if (silent) {
          throw new Error(errMsg);
        }
      }
    } catch (err: any) {
      console.error(err);
      const errMsg = err.message || String(err);
      setPdfProgressError(errMsg);
      if (silent) {
        throw err;
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Dry-Run: Validar todos os seletores sem gravar vídeo/áudio
  const handleDryRun = async () => {
    if (!selectedSubtopicoId) return;
    setIsDryRunning(true);
    setDryRunReport(null);
    setSimulationLogs(['🧪 Dry-Run: Validando seletores...']);
    setSimulationSuccess(null);
    setActiveTab('simulacao');

    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/dry-run`);
      const data = res.data;

      if (data.ok && data.result) {
        const r = data.result;
        setDryRunReport(r.report);

        const logs = [
          `🧪 Dry-Run concluído em ${(r.durationMs / 1000).toFixed(1)}s`,
          `📊 Total: ${r.totalPassos} passos | ✅ ${r.aprovados} OK | ❌ ${r.falhas} Falhas | ⏭️ ${r.pulados} Sem seletor`,
          '',
        ];

        for (const item of r.report) {
          const icon = item.status === 'PASS' ? '✅' : item.status === 'FAIL' ? '❌' : '⏭️';
          logs.push(`${icon} [${item.ordem}] ${item.acao} — ${item.motivo || ''}`);
        }

        if (r.success) {
          logs.push('', '🎉 Todos os seletores validados com sucesso!');
          setSimulationSuccess(true);
        } else {
          logs.push('', `⚠️ ${r.falhas} seletor(es) com problema. Corrija antes de simular.`);
          setSimulationSuccess(false);
        }

        // Adicionar resumo final AOS logs já acumulados via WebSocket (tempo real)
        setSimulationLogs(prev => [
          ...prev,
          '',
          `🧪 Dry-Run concluído em ${(r.durationMs / 1000).toFixed(1)}s`,
          `📊 Total: ${r.totalPassos} passos | ✅ ${r.aprovados} OK | ❌ ${r.falhas} Falhas | ⏭️ ${r.pulados} Sem seletor`,
          r.success ? '🎉 Todos os seletores validados com sucesso!' : `⚠️ ${r.falhas} seletor(es) com problema. Corrija antes de simular.`,
        ]);

        // Salvar screenshots nos cards dos passos (imagemReal)
        // E marcar erros diretamente nos passos
        const updatedPassos = passos.map(p => {
          const reportItem = r.report.find((ri: any) => ri.ordem === p.ordem);
          if (!reportItem) return p;

          const updated = { ...p };

          // Salvar screenshot como imagemReal se tiver
          if (reportItem.screenshot) {
            updated.imagemReal = `data:image/jpeg;base64,${reportItem.screenshot}`;
          }

          // Marcar erro no passo
          if (reportItem.status === 'FAIL') {
            updated.dryRunStatus = 'FAIL';
            updated.dryRunMotivo = reportItem.motivo;
          } else if (reportItem.status === 'PASS') {
            updated.dryRunStatus = 'PASS';
            updated.dryRunMotivo = undefined;
          } else {
            updated.dryRunStatus = reportItem.status;
            updated.dryRunMotivo = undefined;
          }

          return updated;
        });

        // Atualizar passos com screenshots e status
        // Usar o setter direto para atualizar localmente (sem salvar no banco)
        setPassos(updatedPassos as any);

        // Mostrar screenshot do primeiro erro no stream viewer
        const primeiroErro = r.report.find((ri: any) => ri.status === 'FAIL' && ri.screenshot);
        if (primeiroErro) {
          const imgUrl = `data:image/jpeg;base64,${primeiroErro.screenshot}`;
          const canvas = useEcadStore.getState().streamCanvas;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              const img = new Image();
              img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              };
              img.src = imgUrl;
            }
          }
          setStreamFrame('active');
          setActiveStepOrdem(primeiroErro.ordem);
        }

        // Auto-injetar resultado no chat do Copilot
        const falhas = r.report.filter((item: any) => item.status === 'FAIL');
        let chatMsg = `🧪 **Dry-Run concluído** — ${r.aprovados}✅ ${r.falhas}❌ ${r.pulados}⏭️ em ${(r.durationMs / 1000).toFixed(1)}s`;
        if (falhas.length > 0) {
          chatMsg += `\n\n**Passos com falha:**\n`;
          for (const f of falhas) {
            chatMsg += `- **[${f.ordem}]** \`${f.acao}\` — seletor: \`${f.selectorOriginal}\` → ${f.motivo}\n`;
          }
          chatMsg += `\n💡 *Pergunte-me para sugerir correções para os seletores acima.*`;
        } else {
          chatMsg += `\n\n🎉 Todos os seletores validados com sucesso!`;
        }
        setCopilotMessages(prev => [...prev, { role: 'assistant', text: chatMsg }]);

        // Salvar relatório completo e abrir painel
        setDryRunFullReport({
          report: r.report,
          durationMs: r.durationMs,
          aprovados: r.aprovados,
          falhas: r.falhas,
          pulados: r.pulados,
        });
        setShowDryRunPanel(true);
      }
    } catch (err: any) {
      setSimulationLogs(prev => [...prev, `❌ Erro no dry-run: ${err.message || String(err)}`]);
      setSimulationSuccess(false);
    } finally {
      setIsDryRunning(false);
    }
  };

  // Retry: Re-executa um passo individual
  const handleRetryStep = async (ordem: number) => {
    if (!selectedSubtopicoId) return;
    setRetryingStep(ordem);
    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/dry-run/retry-step`, { ordem });
      const data = res.data;
      if (data.ok && data.result) {
        const r = data.result;
        // Atualizar o report local
        if (dryRunFullReport) {
          const updatedReport = dryRunFullReport.report.map((item: any) => {
            if (item.ordem === ordem) {
              return { ...item, status: r.status, motivo: r.motivo, screenshot: r.screenshot };
            }
            return item;
          });
          const newAprovados = updatedReport.filter((i: any) => i.status === 'PASS').length;
          const newFalhas = updatedReport.filter((i: any) => i.status === 'FAIL').length;
          setDryRunFullReport({
            ...dryRunFullReport,
            report: updatedReport,
            aprovados: newAprovados,
            falhas: newFalhas,
          });
        }
        // Atualizar o card do passo
        setPassos(prev => prev.map(p => {
          if (p.ordem === ordem) {
            return {
              ...p,
              dryRunStatus: r.status,
              dryRunMotivo: r.status === 'FAIL' ? r.motivo : undefined,
              imagemReal: r.screenshot ? `data:image/jpeg;base64,${r.screenshot}` : p.imagemReal,
            };
          }
          return p;
        }));

        const icon = r.status === 'PASS' ? '✅' : '❌';
        setCopilotMessages(prev => [...prev, {
          role: 'assistant',
          text: `🔄 **Retry passo ${ordem}** — ${icon} ${r.motivo}`,
        }]);
      }
    } catch (err: any) {
      setCopilotMessages(prev => [...prev, {
        role: 'assistant',
        text: `🔄 ❌ Erro ao repetir passo ${ordem}: ${err.message}`,
      }]);
    } finally {
      setRetryingStep(null);
    }
  };

  // Open Paused: Abre browser visível no ponto do erro
  const handleOpenPaused = async (ordem: number) => {
    if (!selectedSubtopicoId) return;
    setOpeningStep(ordem);
    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/dry-run/open-paused`, { ordem });
      const data = res.data;
      if (data.ok && data.result) {
        setCopilotMessages(prev => [...prev, {
          role: 'assistant',
          text: `🖥️ Browser de debug aberto em **${data.result.url}** para o passo ${ordem}. Feche a janela quando terminar.`,
        }]);
      }
    } catch (err: any) {
      setCopilotMessages(prev => [...prev, {
        role: 'assistant',
        text: `🖥️ ❌ Erro ao abrir browser: ${err.message}`,
      }]);
    } finally {
      setOpeningStep(null);
    }
  };

  // Edit from DryRun: Foca no card do passo e abre modo edição
  const handleEditFromDryRun = (ordem: number) => {
    const index = passos.findIndex(p => p.ordem === ordem);
    if (index >= 0) {
      setShowDryRunPanel(false);
      // Scroll to the card
      setTimeout(() => {
        const el = document.getElementById(`step-card-${index}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.4)';
          setTimeout(() => { el.style.boxShadow = ''; }, 2000);
        }
      }, 100);
    }
  };

  // Nova sessão do Copilot (salva histórico + limpa chat)
  const handleNewCopilotSession = (label?: string) => {
    // Salvar sessão atual no histórico
    if (copilotMessages.length > 1) {
      const sessionLabel = label || `Sessão ${new Date().toLocaleString('pt-BR')}`;
      setCopilotSessionHistory(prev => [
        {
          id: activeSessionId,
          label: sessionLabel,
          timestamp: new Date().toISOString(),
          messages: [...copilotMessages],
        },
        ...prev,
      ]);
    }

    // Criar nova sessão
    const newId = `session-${Date.now()}`;
    setActiveSessionId(newId);

    // Limpar chat com mensagem de boas-vindas
    const perfilObj = perfis.find(p => p.id === selectedPerfilId);
    setCopilotMessages([
      {
        role: 'assistant',
        text: perfilObj
          ? getWelcomeMessageForPerfil(perfilObj.nome)
          : 'Nova sessão iniciada. Como posso ajudar?',
      },
    ]);
    setProposedUiActions(null);
    setDryRunReport(null);
  };

  // Iniciar Simulação Playwright
  const handleRunSimulation = async (startFromStepOrdem?: number, startPaused = false) => {
    if (!selectedSubtopicoId) return;
    setIsSimulating(true);
    setSimulationSuccess(null);
    setActiveTab('simulacao');
    setSimulationLogs([
      `Iniciando navegador Playwright...`,
      startFromStepOrdem
        ? `Pulando para executar a partir do Passo ${startFromStepOrdem}...`
        : 'Injetando sessão de teste homologado...'
    ]);

    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/test`, {
        headless: !simulationModeVisible,
        slowMo: 60,
        startFromStepOrdem: typeof startFromStepOrdem === 'number' ? startFromStepOrdem : undefined,
        startPaused
      });
      const data = res.data;

      if (data.ok && data.result) {
        const result = data.result;
        const newLogs = [...simulationLogs];

        if (result.logs && Array.isArray(result.logs)) {
          newLogs.push(...result.logs);
        }

        if (result.success) {
          newLogs.push('🎉 Simulação concluída com 100% de sucesso!');
          setSimulationSuccess(true);
          if (result.videoUrl) {
            setRoteiroEditForm(prev => ({ ...prev, videoUrl: result.videoUrl }));
            if (selectedRoteiro) {
              setSelectedRoteiro({ ...selectedRoteiro, videoUrl: result.videoUrl });
            }
          }

          // Geração de PDF automática ao concluir com sucesso
          newLogs.push('📄 Iniciando geração do novo Manual PDF automaticamente...');
          setSimulationLogs([...newLogs]); // Atualiza o console com a mensagem

          try {
            await handleGeneratePdf(true);
            newLogs.push('🎉 Manual PDF gerado e atualizado com sucesso!');
          } catch (pdfErr: any) {
            newLogs.push(`⚠️ Falha ao gerar manual PDF automaticamente: ${pdfErr.message || String(pdfErr)}`);
          }
        } else {
          newLogs.push(`❌ ERRO NA SIMULAÇÃO: ${result.error || 'Falha em passo de automação'}`);
          setSimulationSuccess(false);
        }
        setSimulationLogs(newLogs);
      } else {
        setSimulationLogs(prev => [
          ...prev,
          `❌ Falha ao iniciar simulação no backend.`,
          extractErrorMessage(data)
        ]);
        setSimulationSuccess(false);
      }
    } catch (err: any) {
      let errorDetail = err.message || String(err);
      if (err.response) {
        const status = err.response.status;
        const statusText = err.response.statusText || '';
        const data = err.response.data;

        let serverMessage = '';
        if (data) {
          if (typeof data === 'string') {
            serverMessage = data.substring(0, 300);
          } else if (typeof data === 'object') {
            serverMessage = data.message || data.error || JSON.stringify(data);
          }
        }
        errorDetail = `[HTTP ${status} ${statusText}] ${serverMessage || 'Falha de comunicação com o servidor'}`;
      }
      setSimulationLogs(prev => [...prev, `❌ Falha ao se conectar com a API de simulação: ${errorDetail}`]);
      setSimulationSuccess(false);
    } finally {
      setIsSimulating(false);
    }
  };

  const handleAddPasso = () => {
    const newPasso: EcadAutomacaoPasso = {
      ordem: passos.length + 1,
      tipoAcao: 'EXPLICAR',
      selectorCss: '',
      valor: '',
      legenda: ''
    };
    setPassos([...passos, newPasso]);
  };

  const handleInsertPasso = (index: number) => {
    const newPasso: EcadAutomacaoPasso = {
      ordem: index + 1,
      tipoAcao: 'EXPLICAR',
      selectorCss: '',
      valor: '',
      legenda: '',
      isNewStepTemp: true
    } as any;
    const updated = [...passos];
    updated.splice(index, 0, newPasso);
    const reordered = updated.map((p, idx) => ({
      ...p,
      ordem: idx + 1
    }));
    setPassos(reordered);
  };

  const handleRemovePasso = async (index: number) => {
    const updated = passos.filter((_, i) => i !== index);
    setPassos(updated);
    await handleSavePassos(updated, true);
  };

  const handleMovePasso = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === passos.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...passos];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;

    setPassos(updated);
    await handleSavePassos(updated, true);
  };

  const handleUpdatePassoField = (index: number, field: keyof EcadAutomacaoPasso, value: any) => {
    const updated = [...passos];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setPassos(updated);
  };

  const handleToggleSelectStep = (index: number) => {
    setSelectedStepIndexes(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleToggleSelectAllSteps = () => {
    if (selectedStepIndexes.length === passos.length) {
      setSelectedStepIndexes([]);
    } else {
      setSelectedStepIndexes(passos.map((_, idx) => idx));
    }
  };

  const handleClearStepSelection = () => {
    setSelectedStepIndexes([]);
  };

  const handleBulkDeleteSteps = async () => {
    if (selectedStepIndexes.length === 0) return;
    if (!confirm(`Tem certeza que deseja excluir permanentemente os ${selectedStepIndexes.length} passos selecionados?`)) {
      return;
    }
    const updated = passos.filter((_, idx) => !selectedStepIndexes.includes(idx));
    setSelectedStepIndexes([]);
    setPassos(updated);
    await handleSavePassos(updated, true);
  };

  const handleExecuteUiActions = async (actions: any[]) => {
    let updatedPassos = [...passos];
    let needsSave = false;

    for (const action of actions) {
      if (action.action === 'SCROLL') {
        const index = Number(action.index);
        setTimeout(() => {
          const el = document.getElementById(`step-card-${index}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      else if (action.action === 'HIGHLIGHT') {
        const index = Number(action.index);
        setTimeout(() => {
          const el = document.getElementById(`step-card-${index}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const originalOutline = el.style.outline;
            const originalShadow = el.style.boxShadow;
            el.style.outline = '3px solid #c084fc';
            el.style.boxShadow = '0 0 20px rgba(192, 132, 252, 0.6)';
            setTimeout(() => {
              el.style.outline = originalOutline;
              el.style.boxShadow = originalShadow;
            }, 2500);
          }
        }, 150);
      }
      else if (action.action === 'SELECT') {
        if (Array.isArray(action.indexes)) {
          setSelectedStepIndexes(action.indexes.map(Number));
        }
      }
      else if (action.action === 'EDIT') {
        const index = Number(action.index);
        if (updatedPassos[index] && action.fields) {
          updatedPassos[index] = {
            ...updatedPassos[index],
            ...action.fields
          };
          needsSave = true;
        }
      }
      else if (action.action === 'DELETE') {
        if (Array.isArray(action.indexes)) {
          const deleteIdxs = action.indexes.map(Number);
          updatedPassos = updatedPassos.filter((_, idx) => !deleteIdxs.includes(idx));
          needsSave = true;
        }
      }
      else if (action.action === 'ADD') {
        const newStep: EcadAutomacaoPasso = {
          ordem: updatedPassos.length + 1,
          tipoAcao: normalizeTipoAcao(action.fields?.tipoAcao || 'EXPLICAR'),
          selectorCss: action.fields?.selectorCss || '',
          valor: action.fields?.valor || '',
          legenda: action.fields?.legenda || '',
          urlAtual: action.fields?.urlAtual || '',
          autenticacaoNecessaria: action.fields?.autenticacaoNecessaria || false
        };
        updatedPassos.push(newStep);
        needsSave = true;
      }
      else if (action.action === 'INSERT_AT') {
        // Insere novos passos na posição especificada, empurrando os existentes
        const insertIndex = Number(action.index);
        const stepsToInsert: EcadAutomacaoPasso[] = (action.steps || []).map((s: any, i: number) => ({
          ordem: insertIndex + i + 1,
          tipoAcao: normalizeTipoAcao(s.tipoAcao || 'EXPLICAR'),
          selectorCss: typeof s.selectorCss === 'string' ? s.selectorCss.trim() : '',
          valor: sanitizeAcaoValor(s.tipoAcao || '', typeof s.valor === 'string' ? s.valor.trim() : ''),
          legenda: typeof s.legenda === 'string' ? s.legenda.trim() : '',
          urlAtual: typeof s.urlAtual === 'string' ? s.urlAtual.trim() : '',
          autenticacaoNecessaria: !!s.autenticacaoNecessaria
        }));
        const clampedIndex = Math.min(insertIndex, updatedPassos.length);
        updatedPassos.splice(clampedIndex, 0, ...stepsToInsert);
        needsSave = true;
      }
      else if (action.action === 'REPLACE_RANGE') {
        // Substitui um intervalo de passos por novos
        const fromIndex = Number(action.fromIndex);
        const toIndex = Number(action.toIndex);
        const replacementSteps: EcadAutomacaoPasso[] = (action.steps || []).map((s: any) => ({
          ordem: 0,
          tipoAcao: normalizeTipoAcao(s.tipoAcao || 'EXPLICAR'),
          selectorCss: typeof s.selectorCss === 'string' ? s.selectorCss.trim() : '',
          valor: sanitizeAcaoValor(s.tipoAcao || '', typeof s.valor === 'string' ? s.valor.trim() : ''),
          legenda: typeof s.legenda === 'string' ? s.legenda.trim() : '',
          urlAtual: typeof s.urlAtual === 'string' ? s.urlAtual.trim() : '',
          autenticacaoNecessaria: !!s.autenticacaoNecessaria
        }));
        const deleteCount = Math.max(0, toIndex - fromIndex + 1);
        updatedPassos.splice(fromIndex, deleteCount, ...replacementSteps);
        needsSave = true;
      }
      else if (action.action === 'MOVE') {
        // Move um passo de uma posição para outra
        const from = Number(action.fromIndex);
        const to = Number(action.toIndex);
        if (from >= 0 && from < updatedPassos.length && to >= 0 && to < updatedPassos.length) {
          const [moved] = updatedPassos.splice(from, 1);
          updatedPassos.splice(to, 0, moved);
          needsSave = true;
        }
      }
    }

    // Renumerar ordens sequencialmente
    if (needsSave) {
      updatedPassos = updatedPassos.map((p, idx) => ({ ...p, ordem: idx + 1 }));
      setPassos(updatedPassos);
      await handleSavePassos(updatedPassos, true);
    }
    setProposedUiActions(null);
  };

  async function handleSendCopilotMessage(
    overrideMsg?: string,
    overrideFlags?: {
      includePerfil?: boolean;
      includeTopico?: boolean;
      includeSubtopico?: boolean;
      includePassos?: boolean;
    },
    overrideRoteiro?: Subtopico
  ) {
    const msgToSend = typeof overrideMsg === 'string' ? overrideMsg : copilotInput;
    if (!msgToSend.trim()) return;

    const userMsg = msgToSend.trim();
    setCopilotMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    if (typeof overrideMsg !== 'string') {
      setCopilotInput('');
    }
    setCopilotLoading(true);

    try {
      const parseStudioActions = (text: string): any[] | null => {
        try {
          const match = text.match(/```json\s*([\s\S]*?)\s*```/);
          if (match && match[1]) {
            const parsed = JSON.parse(match[1]);
            if (parsed && Array.isArray(parsed.studioActions)) {
              return parsed.studioActions;
            }
          }
          const jsonMatch = text.match(/\{\s*"studioActions"[\s\S]*?\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed && Array.isArray(parsed.studioActions)) {
              return parsed.studioActions;
            }
          }
        } catch (e) {
          // ignore
        }
        return null;
      };

      const contextInfo: any = {};

      const usePerfil = overrideFlags?.includePerfil !== undefined ? overrideFlags.includePerfil : includePerfilContext;
      const useTopico = overrideFlags?.includeTopico !== undefined ? overrideFlags.includeTopico : includeTopicoContext;
      const useSubtopico = overrideFlags?.includeSubtopico !== undefined ? overrideFlags.includeSubtopico : includeSubtopicoContext;
      const usePassos = overrideFlags?.includePassos !== undefined ? overrideFlags.includePassos : includePassosContext;

      // Resolve Perfil
      const activeRoteiroObj = overrideRoteiro || selectedRoteiro;
      let resolvedPerfil = activeRoteiroObj?.topico?.perfil;
      if (!resolvedPerfil && selectedPerfilId) {
        resolvedPerfil = perfis.find(p => p.id === selectedPerfilId);
      }
      if (usePerfil && resolvedPerfil) {
        contextInfo.perfil = {
          id: resolvedPerfil.id,
          nome: resolvedPerfil.nome,
          isInterno: resolvedPerfil.isInterno,
          topicosExistentes: resolvedPerfil.topicos?.map((t: any) => ({
            id: t.id,
            titulo: t.titulo
          })) || []
        };
      }

      // Resolve Topico
      let resolvedTopico = activeRoteiroObj?.topico;
      if (!resolvedTopico && selectedTopicoId && resolvedPerfil) {
        resolvedTopico = resolvedPerfil.topicos?.find((t: any) => t.id === selectedTopicoId);
      }
      if (useTopico && resolvedTopico) {
        contextInfo.topico = {
          id: resolvedTopico.id,
          titulo: resolvedTopico.titulo,
          subtopicosExistentes: resolvedTopico.subtopicos?.map((s: any) => ({
            id: s.id,
            titulo: s.titulo,
            descricao: s.descricao
          })) || []
        };
      }

      // Resolve Subtopico / Roteiro
      if (useSubtopico && activeRoteiroObj) {
        contextInfo.roteiroSubtopico = {
          id: activeRoteiroObj.id,
          titulo: activeRoteiroObj.titulo,
          descricao: activeRoteiroObj.descricao
        };
      }

      if (usePassos) {
        contextInfo.passosAtuais = passos.map(p => ({
          ordem: p.ordem,
          tipoAcao: p.tipoAcao,
          selectorCss: p.selectorCss,
          valor: p.valor,
          legenda: p.legenda,
          urlAtual: p.urlAtual || '',
          autenticacaoNecessaria: p.autenticacaoNecessaria || false
        }));

        if (selectedStepIndexes.length > 0) {
          contextInfo.passosSelecionadosUsuario = selectedStepIndexes.map(idx => {
            const p = passos[idx];
            return p ? {
              ordem: p.ordem,
              indexOriginal: idx,
              tipoAcao: p.tipoAcao,
              selectorCss: p.selectorCss,
              valor: p.valor,
              legenda: p.legenda,
              urlAtual: p.urlAtual || '',
              autenticacaoNecessaria: p.autenticacaoNecessaria || false
            } : null;
          }).filter(Boolean);
        }

        if (activeEditingIndex !== null) {
          contextInfo.passoSendoEditadoOuAdicionado = {
            ...passos[activeEditingIndex],
            ordem: activeEditingIndex + 1,
            indexOriginal: activeEditingIndex
          };
        } else if (activeStepOrdem !== null) {
          contextInfo.passoExecutandoNoSimulador = {
            ...passos[activeStepOrdem - 1],
            ordem: activeStepOrdem,
            indexOriginal: activeStepOrdem - 1
          };
        }
      }

      // Injetar resultado do último dry-run no contexto (se existir)
      if (dryRunReport && dryRunReport.length > 0) {
        const falhas = dryRunReport.filter(r => r.status === 'FAIL');
        const aprovados = dryRunReport.filter(r => r.status === 'PASS');
        contextInfo.ultimoDryRun = {
          resumo: `${aprovados.length} OK, ${falhas.length} FALHAS de ${dryRunReport.length} passos`,
          falhas: falhas.map(f => ({
            passo: f.ordem,
            acao: f.acao,
            seletor: f.selectorOriginal,
            seletorResolvido: f.selectorResolvido,
            motivo: f.motivo
          })),
          passosOk: aprovados.map(p => p.ordem)
        };
      }

      // Injetar logs da simulação se existirem
      if (simulationLogs.length > 0) {
        contextInfo.logsDaUltimaExecucao = simulationLogs.slice(-30);
      }

      const isRoteiroSelected = !!(selectedSubtopicoId || activeRoteiroObj?.id);
      let systemInstruction = '';

      if (isRoteiroSelected) {
        systemInstruction = `Você é o Engenheiro de Automação AI do Aluno Conectado.
Você ajuda o operador a construir e refatorar passos de teste/simulação usando o Playwright.
O Aluno Conectado é uma plataforma com telas de: Login, Presença de Aluno (chamada), Notas de Professores, Ocorrências, etc.

Ao sugerir passos de automação do roteiro, responda com uma breve explicação e depois obrigatoriamente inclua um bloco de código JSON contendo os passos no seguinte formato estruturado:
\`\`\`json
[
  {
    "ordem": 1,
    "tipoAcao": "ESPERAR_PAGINA",
    "selectorCss": "",
    "valor": "/login",
    "legenda": "Alice acessa a tela de Login"
  },
  {
    "ordem": 2,
    "tipoAcao": "DIGITAR",
    "selectorCss": "#username",
    "valor": "admin",
    "legenda": "Alice digita o usuário 'admin'"
  },
  {
    "ordem": 3,
    "tipoAcao": "MOVER_E_CLICAR",
    "selectorCss": "#login-button",
    "valor": "",
    "legenda": "Alice clica no botão 'Entrar'"
  }
]
\`\`\`

REGRA CRÍTICA DE URL: NUNCA use a extensão '.html' para as páginas nas ações ESPERAR_PAGINA (ex: use '/login', não 'login.html'). Em vez disso, use caminhos limpos como '/login', '/admin/dashboard', '/responsavel/dashboard', etc.

Se você precisar controlar a tela do estúdio (ex: rolar para algum passo, destacar um passo, selecionar passos, editar campos, deletar passos, inserir passos ou substituir um trecho), você pode sugerir ações no estúdio incluindo no final da resposta um bloco de código JSON específico contendo as ações:
\`\`\`json
{
  "studioActions": [
    { "action": "SCROLL", "index": 2 },
    { "action": "HIGHLIGHT", "index": 2 },
    { "action": "SELECT", "indexes": [1, 2] },
    { "action": "EDIT", "index": 2, "fields": { "valor": "novo_valor" } },
    { "action": "DELETE", "indexes": [0, 1] },
    { "action": "ADD", "fields": { "tipoAcao": "MOVER_E_CLICAR", "selectorCss": "#btn", "legenda": "Novo passo" } },
    { "action": "INSERT_AT", "index": 14, "steps": [{ "tipoAcao": "DIGITAR", "selectorCss": "#nome", "valor": "teste", "legenda": "Digita nome" }] },
    { "action": "REPLACE_RANGE", "fromIndex": 14, "toIndex": 15, "steps": [{ "tipoAcao": "EXPLICAR", "selectorCss": "", "valor": "", "legenda": "Nova explicação" }] },
    { "action": "MOVE", "fromIndex": 5, "toIndex": 2 }
  ]
}
\`\`\`
As ações válidas no estúdio são:
- SCROLL: Rola até o card do passo (index base-0)
- HIGHLIGHT: Destaca visualmente o card do passo (index base-0)
- SELECT: Seleciona múltiplos passos informados (array de index base-0)
- EDIT: Modifica os campos do passo (index base-0, fields pode conter tipoAcao, selectorCss, valor, legenda, urlAtual, autenticacaoNecessaria)
- DELETE: Exclui os passos informados (array de index base-0). Use para remover passos específicos.
- ADD: Adiciona um novo passo no final (fields pode conter tipoAcao, selectorCss, valor, legenda, urlAtual, autenticacaoNecessaria)
- INSERT_AT: Insere novos passos na posição especificada (index base-0), empurrando os existentes para baixo. Use "steps" array com os passos a inserir.
- REPLACE_RANGE: Substitui os passos do fromIndex ao toIndex (ambos base-0, inclusive) por novos passos. Use "steps" array com os passos substitutos.
- MOVE: Move um passo de fromIndex para toIndex (ambos base-0).

REGRA CRÍTICA DE MERGE: Quando o operador pedir para ADICIONAR ou COMPLEMENTAR passos a um roteiro que JÁ TEM passos existentes, NUNCA substitua tudo com a lista completa. Em vez disso:
1. Use "studioActions" com DELETE para remover passos específicos (se pedido)
2. Use INSERT_AT para inserir novos passos na posição correta preservando os existentes
3. Use REPLACE_RANGE para substituir apenas o trecho que precisa mudar
4. Combine múltiplas ações na mesma lista de studioActions (DELETE primeiro, depois INSERT_AT)
Exemplo: Se o roteiro tem 16 passos e o operador quer adicionar passos detalhados a partir do passo 15:
\`\`\`json
{
  "studioActions": [
    { "action": "DELETE", "indexes": [14, 15] },
    { "action": "INSERT_AT", "index": 14, "steps": [... novos passos ...] }
  ]
}
\`\`\`

Nunca altere a tela diretamente; suas sugestões serão analisadas e confirmadas pelo operador.
As ações suportadas na simulação são: EXPLICAR, DESTACAR, ROLAR_PARA, ROLAR_TOPO, MOVER_E_CLICAR, DIGITAR, ESPERAR_TEMPO, ESPERAR_PAGINA, RESOLVER_CAPTCHA.
Seja preciso nos seletores. O host local roda o site em http://localhost:5502 e o painel React em http://localhost:3000.`;
      } else {
        const perfilNome = resolvedPerfil ? resolvedPerfil.nome : 'Administrador';
        systemInstruction = `Você é o Planejador de Capacitação AI do Aluno Conectado.
Você ajuda o operador a planejar a estrutura de Tópicos e Subtópicos de capacitação para o perfil selecionado.
O perfil selecionado no momento é: "${perfilNome}".

Sua tarefa é sugerir uma hierarquia clara de Tópicos Principais e Roteiros (Subtópicos) para este perfil. Para acelerar a resposta e evitar timeouts, NÃO gere os passos detalhados agora (mantenha o campo "passos" como um array vazio []). Os passos de cada roteiro serão gerados sob demanda posteriormente quando o operador selecionar o roteiro no estúdio.

Retorne obrigatoriamente a sua resposta contendo uma explicação amigável das sugestões, e no final inclua um bloco de código JSON estruturado no seguinte formato:
\`\`\`json
[
  {
    "titulo": "Nome do Tópico Principal",
    "subtopicos": [
      {
        "titulo": "Nome do Roteiro / Subtópico",
        "descricao": "Descrição curta do que o usuário aprende a fazer neste roteiro",
        "passos": []
      }
    ]
  }
]
\`\`\`

REGRA CRÍTICA DE URL: NUNCA use a extensão '.html' para as páginas nas ações ESPERAR_PAGINA (ex: use '/login', não 'login.html'). Em vez disso, use caminhos limpos como '/login', '/admin/dashboard', '/responsavel/dashboard', etc.

As ações suportadas para os passos lógicos são: EXPLICAR, DESTACAR, ROLAR_PARA, ROLAR_TOPO, MOVER_E_CLICAR, DIGITAR, ESPERAR_TEMPO, ESPERAR_PAGINA, RESOLVER_CAPTCHA.

Se o usuário solicitar mover/reordenar tópicos, renomear um item (tópico ou roteiro) ou arquivar um item, você deve obrigatoriamente responder com o seguinte formato JSON de controle correspondente no final da sua resposta:

- Para mover/reordenar tópicos (orderedTopicIds é a lista ordenada de IDs dos tópicos do perfil ativo):
\`\`\`json
{
  "controlType": "MOVE_TOPICS",
  "orderedTopicIds": ["dados_de_acesso", "acesso_municipal"],
  "explanation": "Mudança da ordem visual solicitada pelo operador"
}
\`\`\`

- Para renomear um item (itemType é "TOPIC" ou "SUBTOPIC", itemId é o ID atual do item, newTitle é o novo título sugerido):
\`\`\`json
{
  "controlType": "RENAME_ITEM",
  "itemType": "TOPIC",
  "itemId": "cadastro_municipio",
  "oldTitle": "Cadastro Municipio",
  "newTitle": "Cadastro de Municípios"
}
\`\`\`

- Para arquivar um item (itemType é "TOPIC" ou "SUBTOPIC", itemId é o ID do item a ser arquivado):
\`\`\`json
{
  "controlType": "ARCHIVE_ITEM",
  "itemType": "SUBTOPIC",
  "itemId": "dados_de_acesso",
  "title": "Dados de acesso"
}
\`\`\`

Certifique-se de que os nomes de tópicos, subtópicos e seletores/passos lógicos sugeridos sejam altamente precisos e relevantes para as funções e permissões do perfil "${perfilNome}" descritas no contexto.`;
      }

      const res = await api.post('/ai/chat/simple', {
        message: `${userMsg}\n\nContexto Atual do Roteiro:\n${JSON.stringify(contextInfo, null, 2)}`,
        systemInstruction
      });
      const data = res.data;

      if (data?.response) {
        setCopilotMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
        const uiActions = parseStudioActions(data.response);
        if (uiActions && uiActions.length > 0) {
          setProposedUiActions(uiActions);
        }
      } else {
        const errMsg = extractErrorMessage(data);
        setCopilotMessages(prev => [...prev, { role: 'assistant', text: `Desculpe, tive um problema ao processar a resposta: ${errMsg}` }]);
      }
    } catch (err: any) {
      console.error(err);
      setCopilotMessages(prev => [...prev, { role: 'assistant', text: `Erro de conexão: ${err.message || String(err)}` }]);
    } finally {
      setCopilotLoading(false);
    }
  };

  const handleApplyAiSteps = (steps: EcadAutomacaoPasso[]) => {
    const normalizedSteps = steps.map((p, idx) => ({
      ordem: p.ordem || (idx + 1),
      tipoAcao: normalizeTipoAcao(p.tipoAcao),
      selectorCss: typeof p.selectorCss === 'string' ? p.selectorCss.trim() : '',
      valor: sanitizeAcaoValor(p.tipoAcao, typeof p.valor === 'string' ? p.valor.trim() : ''),
      legenda: typeof p.legenda === 'string' ? p.legenda.trim() : '',
      urlAtual: typeof p.urlAtual === 'string' ? p.urlAtual.trim() : '',
      autenticacaoNecessaria: !!p.autenticacaoNecessaria
    }));

    if (passos.length === 0) {
      // Sem passos existentes: aplica direto
      const reordered = normalizedSteps.map((p, idx) => ({ ...p, ordem: idx + 1 }));
      setPassos(reordered);
      handleSavePassos(reordered, true);
      return;
    }

    // Detectar posição de inserção pela menor "ordem" dos passos da IA
    const minOrdem = Math.min(...normalizedSteps.map(s => s.ordem));
    const insertIndex = Math.max(0, minOrdem - 1); // base-0

    // Construir as studioActions automaticamente para MERGE
    const mergeActions: any[] = [];

    // Se a IA gerou studioActions com DELETE, respeitar
    // Caso contrário, fazer INSERT_AT na posição da menor ordem
    if (insertIndex < passos.length) {
      // Substituir do insertIndex em diante
      mergeActions.push({
        action: 'REPLACE_RANGE',
        fromIndex: insertIndex,
        toIndex: passos.length - 1,
        steps: normalizedSteps
      });
    } else {
      // Adicionar ao final
      mergeActions.push({
        action: 'INSERT_AT',
        index: insertIndex,
        steps: normalizedSteps
      });
    }

    setProposedUiActions(mergeActions);
  };

  const handleCreatePerfil = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!perfilForm.id || !perfilForm.nome) return showToast('Campos obrigatórios vazios!', 'error');

    try {
      const res = await api.post('/ecad/perfil', perfilForm);
      const data = res.data;
      if (data.ok) {
        setShowPerfilModal(false);
        setPerfilForm({ id: '', nome: '', isInterno: false });
        onRefreshCatalog();
        showToast('Perfil cadastrado com sucesso!', 'success');
      } else {
        showToast(`Erro ao cadastrar perfil: ` + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      showToast('Falha ao se conectar com o servidor.', 'error');
    }
  };

  const handleCreateTopico = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topicoForm.id || !topicoForm.titulo || !topicoForm.perfilId) return showToast('Campos obrigatórios vazios!', 'error');

    // Client-side duplication check
    const targetPerfil = perfis.find(p => p.id === topicoForm.perfilId);
    if (targetPerfil) {
      const slugExists = targetPerfil.topicos.some(t => t.id === topicoForm.id);
      if (slugExists) {
        showToast(`O ID/Slug "${topicoForm.id}" já está em uso neste perfil!`, 'error');
        return;
      }
    }

    try {
      const res = await api.post('/ecad/topico', topicoForm);
      const data = res.data;
      if (data.ok) {
        setShowTopicoModal(false);
        setTopicoForm({ id: '', perfilId: '', titulo: '' });
        onRefreshCatalog();
        showToast('Tópico cadastrado com sucesso!', 'success');
      } else {
        showToast(`Erro ao cadastrar tópico: ` + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      showToast('Falha ao se conectar com o servidor.', 'error');
    }
  };

  const handleCreateSubtopico = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subtopicoForm.id || !subtopicoForm.titulo || !subtopicoForm.topicoId) return showToast('Campos obrigatórios vazios!', 'error');

    // Client-side duplication check
    const allTopicos = perfis.flatMap(p => p.topicos);
    const targetTopico = allTopicos.find(t => t.id === subtopicoForm.topicoId);
    if (targetTopico) {
      const slugExists = (targetTopico.subtopicos || []).some(s => s.id === subtopicoForm.id);
      if (slugExists) {
        showToast(`O ID/Slug "${subtopicoForm.id}" já está em uso neste tópico!`, 'error');
        return;
      }
    }

    try {
      const res = await api.post('/ecad/roteiro', subtopicoForm);
      const data = res.data;
      if (data.ok) {
        setShowSubtopicoModal(false);
        setSelectedSubtopicoId(subtopicoForm.id);
        setSubtopicoForm({
          id: '',
          topicoId: '',
          titulo: '',
          descricao: '',
          duracao: '2m 30s',
          dificuldade: 'Básico',
          videoUrl: '',
          pdfUrl: ''
        });
        onRefreshCatalog();
        showToast('Roteiro criado com sucesso! Agora você pode adicionar passos de simulação.', 'success');
      } else {
        showToast(`Erro ao criar roteiro: ` + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      showToast('Falha ao se conectar com o servidor.', 'error');
    }
  };

  const handleDeleteSubtopico = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este roteiro e todos os seus passos permanentemente?')) return;

    try {
      const res = await api.delete(`/ecad/roteiro/${id}`);
      const data = res.data;
      if (data.ok) {
        if (selectedSubtopicoId === id) {
          setSelectedSubtopicoId(null);
        }
        onRefreshCatalog();
        showToast('Roteiro removido com sucesso!', 'success');
      } else {
        showToast(`Erro ao remover: ` + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      showToast('Erro de rede.', 'error');
    }
  };

  const handleDeleteTopico = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este tópico permanentemente?')) return;

    try {
      const res = await api.delete(`/ecad/topico/${id}`);
      const data = res.data;
      if (data.ok) {
        onRefreshCatalog();
        showToast('Tópico removido com sucesso!', 'success');
      } else {
        showToast(`Erro ao remover tópico: ` + extractErrorMessage(data), 'error');
      }
    } catch (err) {
      showToast('Erro de rede.', 'error');
    }
  };

  const handleScanPage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scannerForm.url || !scannerForm.pageKey) return showToast('Campos obrigatórios vazios!', 'error');
    setScanningPage(true);
    try {
      const res = await api.post('/ecad/scan-page', scannerForm);
      const data = res.data;
      if (data.ok) {
        setShowScannerModal(false);
        showToast(`Varredura concluída!\nNovos elementos adicionados: ${data.result?.totalAdicionados || 0}`, 'success');
        onRefreshCatalog();
      } else {
        showToast(`Erro ao escanear página: ` + extractErrorMessage(data), 'error');
      }
    } catch (err: any) {
      showToast('Falha ao se conectar com o servidor para escanear página: ' + (err.message || String(err)), 'error');
    } finally {
      setScanningPage(false);
    }
  };

  const extractErrorMessage = (data: any): string => {
    if (!data) return 'Sem resposta do servidor';
    if (data.error) {
      if (typeof data.error === 'object') {
        const errObj = data.error;
        const detailsMsg = errObj.details?.message || errObj.details?.error || errObj.message;
        if (detailsMsg) return String(detailsMsg);
        return String(errObj.message || errObj.code || JSON.stringify(errObj));
      }
      return String(data.error);
    }
    if (data.message) {
      if (Array.isArray(data.message)) {
        return data.message.join(', ');
      }
      if (typeof data.message === 'object') {
        return String(data.message.message || JSON.stringify(data.message));
      }
      return String(data.message);
    }
    if (data.details && typeof data.details === 'object') {
      return String(data.details.message || JSON.stringify(data.details));
    }
    return JSON.stringify(data);
  };

  const handleApplyProposedHierarchy = async (
    checkedSubtopics: Array<{
      topicTitle: string;
      subtopicTitle: string;
      subtopicDesc: string;
      passos?: any[];
    }>
  ) => {
    if (!selectedPerfilId) {
      showToast('Nenhum perfil selecionado.', 'error');
      return;
    }

    const activePerfil = perfis.find(p => p.id === selectedPerfilId);
    if (!activePerfil) {
      showToast('Perfil ativo não encontrado.', 'error');
      return;
    }

    const slugify = (text: string) => {
      return text
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    };

    const topicsMap = new Map<string, string>();

    for (const item of checkedSubtopics) {
      const topicTitle = item.topicTitle.trim();
      if (!topicTitle) continue;

      const topicId = slugify(topicTitle);

      if (!topicsMap.has(topicTitle)) {
        const existingTopic = activePerfil.topicos?.find(
          t => t.id === topicId || t.titulo.toLowerCase() === topicTitle.toLowerCase()
        );

        if (existingTopic) {
          topicsMap.set(topicTitle, existingTopic.id);
        } else {
          try {
            await api.post('/ecad/topico', {
              id: topicId,
              perfilId: selectedPerfilId,
              titulo: topicTitle
            });
            topicsMap.set(topicTitle, topicId);
          } catch (err) {
            console.error(`Erro ao criar tópico ${topicTitle}:`, err);
            topicsMap.set(topicTitle, topicId);
          }
        }
      }

      const resolvedTopicId = topicsMap.get(topicTitle)!;
      const subtopicTitle = item.subtopicTitle.trim();
      const subtopicId = slugify(subtopicTitle);

      const existingTopic = activePerfil.topicos?.find(t => t.id === resolvedTopicId);
      const existingSub = existingTopic?.subtopicos?.find(
        s => s.id === subtopicId || s.titulo.toLowerCase() === subtopicTitle.toLowerCase()
      );

      if (!existingSub) {
        try {
          await api.post('/ecad/roteiro', {
            id: subtopicId,
            topicoId: resolvedTopicId,
            titulo: subtopicTitle,
            descricao: item.subtopicDesc || '',
            duracao: '2m 30s',
            dificuldade: 'Básico',
            videoUrl: '',
            pdfUrl: ''
          });

          if (item.passos && Array.isArray(item.passos) && item.passos.length > 0) {
            const formattedPassos = item.passos.map((p, idx) => ({
              ordem: idx + 1,
              tipoAcao: normalizeTipoAcao(p.tipoAcao || 'CLICK'),
              selectorCss: typeof p.selectorCss === 'string' ? p.selectorCss.trim() : '',
              valor: sanitizeAcaoValor(p.tipoAcao || 'CLICK', typeof p.valor === 'string' ? p.valor.trim() : ''),
              legenda: typeof p.legenda === 'string' ? p.legenda.trim() : '',
              urlAtual: typeof p.urlAtual === 'string' ? p.urlAtual.trim() : '',
              autenticacaoNecessaria: !!p.autenticacaoNecessaria
            }));
            await api.post(`/ecad/roteiro/${subtopicId}/passos`, { passos: formattedPassos });
          }
        } catch (err) {
          console.error(`Erro ao criar subtópico ${subtopicTitle}:`, err);
        }
      } else if (existingSub && (!existingSub.passos || existingSub.passos.length === 0)) {
        if (item.passos && Array.isArray(item.passos) && item.passos.length > 0) {
          try {
            const formattedPassos = item.passos.map((p, idx) => ({
              ordem: idx + 1,
              tipoAcao: normalizeTipoAcao(p.tipoAcao || 'CLICK'),
              selectorCss: typeof p.selectorCss === 'string' ? p.selectorCss.trim() : '',
              valor: sanitizeAcaoValor(p.tipoAcao || 'CLICK', typeof p.valor === 'string' ? p.valor.trim() : ''),
              legenda: typeof p.legenda === 'string' ? p.legenda.trim() : '',
              urlAtual: typeof p.urlAtual === 'string' ? p.urlAtual.trim() : '',
              autenticacaoNecessaria: !!p.autenticacaoNecessaria
            }));
            await api.post(`/ecad/roteiro/${subtopicId}/passos`, { passos: formattedPassos });
          } catch (err) {
            console.error(`Erro ao cadastrar passos para subtópico existente ${subtopicTitle}:`, err);
          }
        }
      }
    }

    onRefreshCatalog();
  };

  const handleRenameEcadItem = async (itemType: 'TOPIC' | 'SUBTOPIC', itemId: string, newTitle: string) => {
    if (!selectedPerfilId) return;
    const activePerfil = perfis.find(p => p.id === selectedPerfilId);
    if (!activePerfil) return;

    if (itemType === 'TOPIC') {
      const topic = activePerfil.topicos?.find(t => t.id === itemId);
      if (!topic) return;
      try {
        await api.post('/ecad/topico', {
          id: itemId,
          perfilId: selectedPerfilId,
          titulo: newTitle
        });
        showToast('Tópico renomeado com sucesso!', 'success');
        onRefreshCatalog();
      } catch (err) {
        showToast('Erro ao renomear tópico.', 'error');
      }
    } else {
      let foundSub: any = null;
      let foundTopicoId = '';
      for (const t of activePerfil.topicos || []) {
        const s = t.subtopicos?.find(sub => sub.id === itemId);
        if (s) {
          foundSub = s;
          foundTopicoId = t.id;
          break;
        }
      }
      if (!foundSub) return;
      try {
        await api.post('/ecad/roteiro', {
          id: itemId,
          topicoId: foundTopicoId,
          titulo: newTitle,
          descricao: foundSub.descricao || '',
          duracao: foundSub.duracao || '2m 30s',
          dificuldade: foundSub.dificuldade || 'Básico',
          videoUrl: foundSub.videoUrl || '',
          pdfUrl: foundSub.pdfUrl || '',
          status: foundSub.status || 'RASCUNHO'
        });
        showToast('Roteiro renomeado com sucesso!', 'success');
        onRefreshCatalog();
      } catch (err) {
        showToast('Erro ao renomear roteiro.', 'error');
      }
    }
  };

  const handleArchiveEcadItem = async (itemType: 'TOPIC' | 'SUBTOPIC', itemId: string) => {
    if (!selectedPerfilId) return;
    const activePerfil = perfis.find(p => p.id === selectedPerfilId);
    if (!activePerfil) return;

    if (itemType === 'TOPIC') {
      const archived = JSON.parse(localStorage.getItem(`ecad_archived_topics_${selectedPerfilId}`) || '[]');
      if (!archived.includes(itemId)) {
        archived.push(itemId);
        localStorage.setItem(`ecad_archived_topics_${selectedPerfilId}`, JSON.stringify(archived));
      }
      showToast('Tópico arquivado com sucesso!', 'success');
      onRefreshCatalog();
    } else {
      let foundSub: any = null;
      let foundTopicoId = '';
      for (const t of activePerfil.topicos || []) {
        const s = t.subtopicos?.find(sub => sub.id === itemId);
        if (s) {
          foundSub = s;
          foundTopicoId = t.id;
          break;
        }
      }
      if (!foundSub) return;
      try {
        await api.post('/ecad/roteiro', {
          id: itemId,
          topicoId: foundTopicoId,
          titulo: foundSub.titulo,
          descricao: foundSub.descricao || '',
          duracao: foundSub.duracao || '2m 30s',
          dificuldade: foundSub.dificuldade || 'Básico',
          videoUrl: foundSub.videoUrl || '',
          pdfUrl: foundSub.pdfUrl || '',
          status: 'ARQUIVADO'
        });
        showToast('Roteiro arquivado com sucesso!', 'success');
        onRefreshCatalog();
      } catch (err) {
        showToast('Erro ao arquivar roteiro.', 'error');
      }
    }
  };

  const handleRestoreEcadItem = async (itemType: 'TOPIC' | 'SUBTOPIC', itemId: string) => {
    if (!selectedPerfilId) return;
    const activePerfil = perfis.find(p => p.id === selectedPerfilId);
    if (!activePerfil) return;

    if (itemType === 'TOPIC') {
      const archived = JSON.parse(localStorage.getItem(`ecad_archived_topics_${selectedPerfilId}`) || '[]');
      const updated = archived.filter((id: string) => id !== itemId);
      localStorage.setItem(`ecad_archived_topics_${selectedPerfilId}`, JSON.stringify(updated));
      showToast('Tópico restaurado com sucesso!', 'success');
      onRefreshCatalog();
    } else {
      let foundSub: any = null;
      let foundTopicoId = '';
      for (const t of activePerfil.topicos || []) {
        const s = t.subtopicos?.find(sub => sub.id === itemId);
        if (s) {
          foundSub = s;
          foundTopicoId = t.id;
          break;
        }
      }
      if (!foundSub) return;
      try {
        await api.post('/ecad/roteiro', {
          id: itemId,
          topicoId: foundTopicoId,
          titulo: foundSub.titulo,
          descricao: foundSub.descricao || '',
          duracao: foundSub.duracao || '2m 30s',
          dificuldade: foundSub.dificuldade || 'Básico',
          videoUrl: foundSub.videoUrl || '',
          pdfUrl: foundSub.pdfUrl || '',
          status: 'RASCUNHO'
        });
        showToast('Roteiro restaurado com sucesso!', 'success');
        onRefreshCatalog();
      } catch (err) {
        showToast('Erro ao restaurar roteiro.', 'error');
      }
    }
  };

  const handleApplyTopicOrder = (orderedIds: string[]) => {
    if (!selectedPerfilId) return;
    setCustomTopicOrder(orderedIds);
    localStorage.setItem(`ecad_custom_topic_order_${selectedPerfilId}`, JSON.stringify(orderedIds));
    showToast('Ordem dos tópicos atualizada visualmente com sucesso!', 'success');
  };

  return {
    selectedSubtopicoId,
    setSelectedSubtopicoId,
    selectedRoteiro,
    setSelectedRoteiro,
    selectedPerfilId,
    setSelectedPerfilId,
    selectedTopicoId,
    setSelectedTopicoId,
    passos,
    setPassos,
    loadingRoteiro,
    savingPassos,
    showPerfilModal,
    setShowPerfilModal,
    showTopicoModal,
    setShowTopicoModal,
    showSubtopicoModal,
    setShowSubtopicoModal,
    perfilForm,
    setPerfilForm,
    topicoForm,
    setTopicoForm,
    subtopicoForm,
    setSubtopicoForm,
    roteiroEditForm,
    setRoteiroEditForm,
    savingRoteiroMeta,
    isEditingMetadata,
    setIsEditingMetadata,
    isEditingPassos,
    setIsEditingPassos,
    zoomImage,
    setZoomImage,
    activeTab,
    setActiveTab,
    snapshots,
    loadingSnapshots,
    restoringSnapshot,
    passosEditMode,
    setPassosEditMode,
    jsonValue,
    setJsonValue,
    generatingPdf,
    showScannerModal,
    setShowScannerModal,
    scannerForm,
    setScannerForm,
    scanningPage,
    isSimulating,
    isPaused,
    simulationLogs,
    simulationSuccess,
    setSimulationSuccess,
    simulationModeVisible,
    setSimulationModeVisible,
    terminalEndRef,
    copilotMessages,
    copilotInput,
    setCopilotInput,
    copilotLoading,
    selectedStepIndexes,
    setSelectedStepIndexes,
    proposedUiActions,
    setProposedUiActions,
    activeEditingIndex,
    setActiveEditingIndex,
    includePerfilContext,
    setIncludePerfilContext,
    includeTopicoContext,
    setIncludeTopicoContext,
    includeSubtopicoContext,
    setIncludeSubtopicoContext,
    includePassosContext,
    setIncludePassosContext,
    chatEndRef,
    handleCancelEditMeta,
    handleSaveRoteiroMeta,
    handleSavePassos,
    handleSaveFromJson,
    fetchSnapshots,
    handleRestoreVersion,
    handleGeneratePdf,
    handleRunSimulation,
    pdfProgressShow,
    setPdfProgressShow,
    pdfProgressStep,
    setPdfProgressStep,
    pdfProgressError,
    setPdfProgressError,
    pdfProgressUrl,
    pdfLogs,
    handleAddPasso,
    handleInsertPasso,
    handleRemovePasso,
    handleMovePasso,
    handleUpdatePassoField,
    handleToggleSelectStep,
    handleToggleSelectAllSteps,
    handleClearStepSelection,
    handleBulkDeleteSteps,
    handleExecuteUiActions,
    handleSendCopilotMessage,
    handleApplyAiSteps,
    handleApplyProposedHierarchy,
    handleCreatePerfil,
    handleCreateTopico,
    handleCreateSubtopico,
    handleDeleteSubtopico,
    handleDeleteTopico,
    handleScanPage,
    showArchivedModal,
    setShowArchivedModal,
    customTopicOrder,
    setCustomTopicOrder,
    handleRenameEcadItem,
    handleArchiveEcadItem,
    handleRestoreEcadItem,
    handleApplyTopicOrder,

    // Novas propriedades de WebSocket Stream e Controle
    activeStepOrdem,
    connected,
    streamFrame,
    currentBrowserUrl,
    handleStopSimulation,
    handleBackBrowser,
    handleForwardBrowser,
    handleReloadBrowser,
    handleNavigateBrowser,
    handleRegeneratePassos,
    handlePauseSimulation,
    handleResumeSimulation,
    handleStepForward,
    handleStepBackward,
    browserInstance,
    handleKillBrowser,
    handleRestartBrowser,
    handleJumpToStep,
    precacheInfo,
    toastMessage,
    toastType,
    showToast,
    uiMap,

    // Dry-Run (validação rápida de seletores)
    isDryRunning,
    dryRunReport,
    handleDryRun,

    // Dry-Run Panel
    showDryRunPanel,
    setShowDryRunPanel,
    dryRunFullReport,
    retryingStep,
    openingStep,
    handleRetryStep,
    handleOpenPaused,
    handleEditFromDryRun,

    // Sessões do Copilot
    copilotSessionHistory,
    activeSessionId,
    handleNewCopilotSession,
  };
}
